import React, { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2, PartyPopper } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useFilters } from "@/context/FilterContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { BlockSkeleton } from "@/components/Loading";
import { StatusBadge } from "@/components/StatusBadge";
import { BrandSelectField } from "@/components/BrandSelectField";
import { ExportButton } from "@/components/ExportButton";
import { fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const STATUSES = ["new", "contacted", "confirmed", "lost"];
const TYPES = ["wedding", "corporate", "birthday", "rehearsal", "other"];

const empty = {
  brand_id: "", contact_name: "", contact_email: "", contact_phone: "",
  event_type: "wedding", event_date: "", guest_count: 20, status: "new", message: "",
};

export default function Inquiries() {
  const { queryParams } = useFilters();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = { ...queryParams };
    if (statusFilter !== "all") params.status = statusFilter;
    api.get("/inquiries", { params })
      .then((res) => setItems(res.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [queryParams, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setForm(empty); setEditingId(null); setDialogOpen(true); };
  const openEdit = (r) => { setForm({ ...r }); setEditingId(r.id); setDialogOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, guest_count: Number(form.guest_count) || 0 };
      if (editingId) { await api.patch(`/inquiries/${editingId}`, payload); toast.success("Inquiry updated"); }
      else { await api.post("/inquiries", payload); toast.success("Inquiry created"); }
      setDialogOpen(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const quickStatus = async (r, status) => {
    try { await api.patch(`/inquiries/${r.id}`, { status }); setItems((prev) => prev.map((x) => x.id === r.id ? { ...x, status } : x)); toast.success("Status updated"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const confirmDelete = async () => {
    try { await api.delete(`/inquiries/${deleteId}`); toast.success("Inquiry deleted"); setDeleteId(null); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="space-y-6">
      <PageHeader title="Event Inquiries" subtitle="Private dining & event lead pipeline.">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger data-testid="inquiries-status-filter" className="h-9 w-[150px] rounded-full border-[var(--pb-border)] bg-white text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <ExportButton resource="inquiries" params={{ ...queryParams, ...(statusFilter !== "all" ? { status: statusFilter } : {}) }} />
        <Button data-testid="add-inquiry-button" onClick={openCreate} className="h-9 gap-2 rounded-full bg-[#18344A] text-white hover:bg-[#18344A]/90">
          <Plus className="h-4 w-4" /> Add inquiry
        </Button>
      </PageHeader>

      <Card className="overflow-hidden rounded-[14px] border-[var(--pb-border)] bg-white shadow-[var(--pb-shadow-sm)]">
        {loading ? (
          <div className="p-4"><BlockSkeleton className="h-96" /></div>
        ) : items.length === 0 ? (
          <EmptyState title="No inquiries found" message="Adjust filters or add a new inquiry." icon={PartyPopper}
            action={<Button onClick={openCreate} className="rounded-full bg-[#18344A] text-white hover:bg-[#18344A]/90"><Plus className="mr-2 h-4 w-4" />Add inquiry</Button>} />
        ) : (
          <div className="overflow-x-auto">
            <Table data-testid="inquiries-table">
              <TableHeader>
                <TableRow className="bg-[rgba(24,52,74,0.04)]">
                  <TableHead>Contact</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Event date</TableHead>
                  <TableHead className="text-right">Guests</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((r) => (
                  <TableRow key={r.id} className="hover:bg-[rgba(183,165,142,0.1)]">
                    <TableCell>
                      <div className="font-medium text-[#30383C]">{r.contact_name}</div>
                      <div className="text-xs text-[rgba(48,56,60,0.55)]">{r.contact_email}</div>
                    </TableCell>
                    <TableCell className="text-sm capitalize">{r.event_type}</TableCell>
                    <TableCell className="text-sm">{fmtDate(r.event_date)}</TableCell>
                    <TableCell className="text-right font-num">{r.guest_count}</TableCell>
                    <TableCell>
                      <Select value={r.status} onValueChange={(v) => quickStatus(r, v)}>
                        <SelectTrigger data-testid={`inquiry-status-${r.id}`} className="h-8 w-[130px] border-none bg-transparent p-0 shadow-none focus:ring-0">
                          <StatusBadge status={r.status} />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" data-testid={`edit-inquiry-${r.id}`} onClick={() => openEdit(r)} className="h-8 w-8 text-[#526E7E]"><Pencil className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" data-testid={`delete-inquiry-${r.id}`} onClick={() => setDeleteId(r.id)} className="h-8 w-8 text-[#9a3a3a]"><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-[#18344A]">{editingId ? "Edit inquiry" : "New inquiry"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4" data-testid="inquiry-form">
            {!editingId && <BrandSelectField value={form.brand_id} onChange={set("brand_id")} />}
            <div className="space-y-1.5">
              <Label>Contact name</Label>
              <Input required data-testid="inquiry-contact-name" value={form.contact_name} onChange={(e) => set("contact_name")(e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={form.contact_email} onChange={(e) => set("contact_email")(e.target.value)} /></div>
              <div className="space-y-1.5"><Label>Phone</Label><Input value={form.contact_phone} onChange={(e) => set("contact_phone")(e.target.value)} /></div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label>Type</Label>
                <Select value={form.event_type} onValueChange={set("event_type")}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Event date</Label><Input type="date" value={form.event_date} onChange={(e) => set("event_date")(e.target.value)} /></div>
              <div className="space-y-1.5"><Label>Guests</Label><Input type="number" min="0" value={form.guest_count} onChange={(e) => set("guest_count")(e.target.value)} /></div>
            </div>
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={set("status")}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Message</Label><Textarea rows={3} value={form.message} onChange={(e) => set("message")(e.target.value)} /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} data-testid="inquiry-form-submit" className="bg-[#18344A] text-white hover:bg-[#18344A]/90">
                {saving ? "Saving\u2026" : editingId ? "Save changes" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete inquiry?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-inquiry" onClick={confirmDelete} className="bg-[#9a3a3a] text-white hover:bg-[#9a3a3a]/90">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
